import { supabase } from '@/lib/supabase/client';
import type { Database } from '@/lib/supabase/types';

type ConnectionRow = Database['public']['Tables']['connections']['Row'];
export type Connection = ConnectionRow;
export type ConnectionStatus = Database['public']['Enums']['connection_status'];

export async function sendConnectionRequest(
  requesterId: string,
  addresseeId: string,
): Promise<Connection> {
  const { data, error } = await supabase
    .from('connections')
    .insert({ requester_id: requesterId, addressee_id: addresseeId, status: 'pending' })
    .select('id, requester_id, addressee_id, status, created_at, updated_at')
    .single()
    .throwOnError();
  if (error) throw error;
  return data!;
}

export async function acceptConnection(connectionId: string): Promise<Connection> {
  const { data, error } = await supabase
    .from('connections')
    .update({ status: 'accepted' })
    .eq('id', connectionId)
    .select('id, requester_id, addressee_id, status, created_at, updated_at')
    .single()
    .throwOnError();
  if (error) throw error;
  return data!;
}

export async function declineConnection(connectionId: string): Promise<Connection> {
  const { data, error } = await supabase
    .from('connections')
    .update({ status: 'declined' })
    .eq('id', connectionId)
    .select('id, requester_id, addressee_id, status, created_at, updated_at')
    .single()
    .throwOnError();
  if (error) throw error;
  return data!;
}

export async function withdrawConnection(connectionId: string): Promise<void> {
  const { error } = await supabase
    .from('connections')
    .delete()
    .eq('id', connectionId)
    .throwOnError();
  if (error) throw error;
}

/** Returns the connection row between two profiles (either direction), or null. */
export async function getConnectionBetween(
  profileIdA: string,
  profileIdB: string,
): Promise<Connection | null> {
  const { data, error } = await supabase
    .from('connections')
    .select('id, requester_id, addressee_id, status, created_at, updated_at')
    .or(
      `and(requester_id.eq.${profileIdA},addressee_id.eq.${profileIdB}),` +
        `and(requester_id.eq.${profileIdB},addressee_id.eq.${profileIdA})`,
    )
    .maybeSingle()
    .throwOnError();
  if (error) throw error;
  return data;
}

/** Returns all accepted connections for a profile (either side of the relation). */
export async function listConnections(profileId: string): Promise<Connection[]> {
  const { data, error } = await supabase
    .from('connections')
    .select('id, requester_id, addressee_id, status, created_at, updated_at')
    .or(`requester_id.eq.${profileId},addressee_id.eq.${profileId}`)
    .eq('status', 'accepted')
    .order('updated_at', { ascending: false })
    .throwOnError();
  if (error) throw error;
  return data ?? [];
}

/** Returns pending requests where the current user is the addressee. */
export async function listPendingRequests(addresseeId: string): Promise<Connection[]> {
  const { data, error } = await supabase
    .from('connections')
    .select('id, requester_id, addressee_id, status, created_at, updated_at')
    .eq('addressee_id', addresseeId)
    .eq('status', 'pending')
    .order('created_at', { ascending: false })
    .throwOnError();
  if (error) throw error;
  return data ?? [];
}
